import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

import { toast } from 'react-toastify';
import {
  useAddEmployeeLeaveMutation,
  useCancelEmployeeLeaveMutation,
  useApproveEmployeeLeaveMutation,
  useRejectEmployeeLeaveMutation,
  useUpdateEmployeeLeaveMutation,
  useGetEmployeeLeavesByYearQuery,
  useGetEmployeeLeavesStatsQuery,
} from 'redux/features/employeeLeaveApi';
import { useGetHolidaysByYearQuery } from 'redux/features/holidayApi';
import RejectLeaveModal from 'pages/admin-leave-management/components/RejectLeaveModal';
import SkeletonWrapper from 'components/ui/SkeletonWrapper';

import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CommandPalette from '../../components/ui/CommandPalette';
import CalendarGrid from './components/CalendarGrid';
import EventListModal from '../holiday-management-admin-view/components/EventListModal';
import AddLeaveModal from './components/AddLeaveModal';
import LeaveSummaryPanel from './components/LeaveSummaryPanel';
import useUser from '../../hooks/useUser';
import ConfirmationModal from '../../components/ui/ConfirmationModal';

const HolidayManagementEmployeeView = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaves, setLeaves] = useState([]);
  const [isListModalOpen, setIsListModalOpen] = useState(false);
  const [isAddLeaveModalOpen, setIsAddLeaveModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [selectedLeaveFilter, setSelectedLeaveFilter] = useState('PENDING');
  const [holidayStats, setHolidayStats] = useState(null);
  const [leaveToEdit, setLeaveToEdit] = useState(null);
  const [rejectLeave, setRejectLeave] = useState(null);
  const [approveLeave, setApproveLeave] = useState(null);
  const [rejectLeaveAdmin, setRejectLeaveAdmin] = useState(null);
  const [processingId, setProcessingId] = useState(null);

  // RTK Queries & Mutations
  const { data: employeeLeaves, isLoading: employeeLeavesLoading } =
    useGetEmployeeLeavesByYearQuery({
      year: currentDate?.getFullYear(),
    });
  const { data: holidaysData, isLoading: holidaysLoading } = useGetHolidaysByYearQuery({
    year: currentDate?.getFullYear(),
  });

  const { data: employeeLeavesStats, isLoading: employeeLeavesStatsLoading } =
    useGetEmployeeLeavesStatsQuery({
      year: currentDate?.getFullYear(),
    });

  const [addEmployeeLeave, { isLoading: isAddingLeave }] = useAddEmployeeLeaveMutation();
  const [updateEmployeeLeave, { isLoading: isUpdatingLeave }] = useUpdateEmployeeLeaveMutation();
  const [cancelEmployeeLeave, { isLoading: isCancelingLeave }] = useCancelEmployeeLeaveMutation();
  // Admin actions (if needed in this view, though usually separate)
  const [approveEmployeeLeave, { isLoading: isApprovingLeave }] = useApproveEmployeeLeaveMutation();
  const [rejectEmployeeLeave, { isLoading: isRejectingLeave }] = useRejectEmployeeLeaveMutation();

  // Desktop right panel toggle state - closed by default on mobile/tablet
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(() => {
    // Check if we're on desktop (lg breakpoint and above)
    return window.innerWidth >= 1024;
  });

  // Calendar filter states with localStorage persistence
  const [calendarFilters, setCalendarFilters] = useState(() => {
    const savedFilters = localStorage.getItem('calendarFilters');
    return savedFilters
      ? JSON.parse(savedFilters)
      : {
          eventCelebration: true,
          fullHoliday: true,
          halfHoliday: true,
          myLeaves: true,
          saturdayOff: true,
        };
  });

  // Get current user session
  const user = useUser();
  const currentEmployeeId = user?.id;
  const isAdmin = user?.role === 'admin' || user?.isAdmin === true;

  useEffect(() => {
    if (holidaysData?.stats) {
      setHolidayStats(holidaysData?.stats);
    }
  }, [currentEmployeeId, holidaysData]);

  // Sync leaves from API to local state
  useEffect(() => {
    if (employeeLeaves) {
      const leavesToShow = isAdmin
        ? employeeLeaves
        : employeeLeaves?.filter((leave) => leave?.user_id === currentEmployeeId) || [];
      setLeaves(leavesToShow);
    }
  }, [employeeLeaves, isAdmin, currentEmployeeId]);

  // Persist filter changes to localStorage
  useEffect(() => {
    localStorage.setItem('calendarFilters', JSON.stringify(calendarFilters));
  }, [calendarFilters]);

  // Handle responsive panel behavior on window resize
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      // Only auto-close on mobile/tablet, don't auto-open on desktop
      if (!isDesktop && isRightPanelOpen) {
        setIsRightPanelOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isRightPanelOpen]);

  const handleDateClick = (date, events, leaves) => {
    setSelectedDate(date);
    setSelectedEvents(events);
    setSelectedLeaves(leaves || []);

    if (events?.length > 0 || leaves?.length > 0) {
      setIsListModalOpen(true);
    } else {
      // Prevent opening add leave modal for past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const clickedDate = new Date(date);
      clickedDate.setHours(0, 0, 0, 0);

      if (clickedDate < today) {
        return;
      }
      setIsAddLeaveModalOpen(true);
    }
  };

  const handleAddLeave = async (leaveData) => {
    try {
      const payload = {
        ...leaveData,
        user_id: currentEmployeeId,
        status: 'PENDING', // Ensure consistent casing with backend if needed
      };
      await addEmployeeLeave(payload).unwrap();
      toast.success('Leave applied successfully');
      // No need to manually update state, RTK Query tags will handle refetch
    } catch (error) {
      console.error('Failed to add leave:', error);
      // Handle error (show toast, etc.)
    }
  };

  const handleApproveLeave = async () => {
    if (!approveLeave) return;
    const leaveId = approveLeave.id;
    setProcessingId(leaveId);
    setApproveLeave(null);
    try {
      await approveEmployeeLeave(leaveId).unwrap();
      toast.success('Leave approved successfully');
    } catch (error) {
      console.error('Failed to approve leave:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectLeaveAdmin = async (reason) => {
    if (!rejectLeaveAdmin) return;
    const leaveId = rejectLeaveAdmin.id;
    setProcessingId(leaveId);
    try {
      await rejectEmployeeLeave({ id: leaveId, reason }).unwrap();
      toast.success('Leave rejected successfully');
      setRejectLeaveAdmin(null);
    } catch (error) {
      console.error('Failed to reject leave:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelLeave = async (reason) => {
    if (!rejectLeave) return;
    setProcessingId(rejectLeave.id);

    try {
      await cancelEmployeeLeave({ id: rejectLeave.id, reason }).unwrap();
      setRejectLeave(null);
      toast.success('Leave cancelled successfully');
    } catch (error) {
      console.error('Failed to cancel leave:', error);
      toast.error(error?.data?.message || 'Failed to cancel leave');
    } finally {
      setProcessingId(null);
    }
  };

  const handleEditLeave = (leave) => {
    setLeaveToEdit(leave);
    setIsAddLeaveModalOpen(true);
  };

  const handleUpdateLeave = async (leaveData) => {
    try {
      await updateEmployeeLeave({ ...leaveData, user_id: currentEmployeeId }).unwrap();
      setLeaveToEdit(null);
    } catch (error) {
      console.error('Failed to update leave:', error);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setCalendarFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleEditEvent = () => {
    // Handle event editing if needed
  };

  const handleDeleteEvent = () => {
    // Handle event deletion if needed
  };

  const handleAddFromList = (date) => {
    // Prevent opening add leave modal for past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    if (targetDate < today) {
      return;
    }
    setIsListModalOpen(false);
    setLeaveToEdit(null);
    setSelectedDate(date);
    setIsAddLeaveModalOpen(true);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(newDate?.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e?.target?.value);
    const newDate = new Date(currentDate);
    newDate?.setMonth(newMonth);
    setCurrentDate(newDate);
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e?.target?.value);
    const newDate = new Date(currentDate);
    newDate?.setFullYear(newYear);
    setCurrentDate(newDate);
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const currentYear = currentDate?.getFullYear();
  const currentMonth = currentDate?.getMonth();

  // Generate year options (current year ± 10 years)
  const currentYearValue = new Date()?.getFullYear();
  const yearOptions = [];
  for (let year = currentYearValue - 10; year <= currentYearValue + 10; year++) {
    yearOptions?.push(year);
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header />
      <Sidebar />

      <CommandPalette />

      {/* Main Content */}
      <div className="lg:ml-60 pt-16 overflow-x-hidden">
        <div className="flex">
          {/* Left Panel - Calendar */}
          <motion.div
            className={`flex-1 px-4 sm:px-6 lg:px-8 py-4 lg:py-8 ${
              isRightPanelOpen ? 'min-w-0' : 'min-w-0'
            }`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Page Header */}
            <motion.div className="mb-6 lg:mb-8" variants={itemVariants}>
              <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-text-primary mb-2">
                Holiday Calendar & Leave Management
              </h1>
              <p className="text-text-secondary text-sm lg:text-base">
                View company holidays and manage your leave applications
              </p>
            </motion.div>

            {/* Statistics Cards - Auto-adjustable Flex */}
            <motion.div
              className="flex flex-wrap gap-3 lg:gap-4 mb-4 lg:mb-6"
              variants={itemVariants}
            >
              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={holidaysLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-primary-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Calendar" size={16} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Total Events
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {holidayStats?.totalHolidayCount}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={holidaysLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[140px] p-2 lg:p-3' : 'min-w-[160px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-blue-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Star" size={16} color="#3B82F6" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Celebrations
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {holidayStats?.eventCelebrationCount}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={holidaysLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-red-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Calendar" size={16} color="#EF4444" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Full Holidays
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {holidayStats?.fullDayHolidayCount}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={holidaysLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-orange-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Clock" size={16} color="#F97316" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Half Holidays
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {holidayStats?.halfDayHolidayCount}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={holidaysLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-green-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="Home" size={16} color="#10B981" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Saturday Off
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {holidayStats?.saturdayOffCount}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
            </motion.div>

            {/* Leave Stats - Auto-adjustable Flex */}
            <motion.div
              className="flex flex-wrap gap-3 lg:gap-4 mb-4 lg:mb-6"
              variants={itemVariants}
            >
              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={employeeLeavesStatsLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-purple-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="FileText" size={16} color="#8B5CF6" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Total Leaves
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {employeeLeavesStats?.total}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={employeeLeavesStatsLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-yellow-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-1.5'
                      }`}
                    >
                      <Icon name="Clock" size={16} color="#F59E0B" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Pending
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {employeeLeavesStats?.pending}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={employeeLeavesStatsLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-green-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="CheckCircle" size={16} color="#10B981" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Accepted
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {employeeLeavesStats?.approved}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={employeeLeavesStatsLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-red-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="XCircle" size={16} color="#EF4444" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Rejected
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {employeeLeavesStats?.rejected}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>

              <SkeletonWrapper
                width="w-full"
                height="h-[80px]"
                className="flex-1"
                isLoading={employeeLeavesStatsLoading}
              >
                <div
                  className={`bg-surface border border-border rounded-lg flex-1 ${
                    isRightPanelOpen ? 'min-w-[120px] p-2 lg:p-3' : 'min-w-[140px] p-3 lg:p-4'
                  }`}
                >
                  <div
                    className={`flex items-center ${
                      isRightPanelOpen ? 'space-x-2' : 'space-x-2 lg:space-x-3'
                    }`}
                  >
                    <div
                      className={`bg-red-100 rounded-lg ${
                        isRightPanelOpen ? 'p-1 lg:p-1.5' : 'p-1.5 lg:p-2'
                      }`}
                    >
                      <Icon name="XCircle" size={16} color="#EF4444" />
                    </div>
                    <div className="flex-1">
                      <p
                        className={`text-text-secondary ${
                          isRightPanelOpen ? 'text-xs' : 'text-xs lg:text-sm'
                        }`}
                      >
                        Cancelled
                      </p>
                      <p
                        className={`font-semibold text-text-primary ${
                          isRightPanelOpen ? 'text-base lg:text-lg' : 'text-lg lg:text-xl'
                        }`}
                      >
                        {employeeLeavesStats?.cancelled}
                      </p>
                    </div>
                  </div>
                </div>
              </SkeletonWrapper>
            </motion.div>

            {/* Calendar Navigation & Filters */}
            <motion.div
              className="bg-surface rounded-lg border border-border p-4 lg:p-6 mb-4 lg:mb-6"
              variants={itemVariants}
            >
              <div className="flex flex-col space-y-4">
                {/* Calendar Navigation */}
                <div className="flex flex-wrap items-center justify-between gap-2 lg:gap-4">
                  {/* Left side - Navigation controls */}
                  <div className="flex flex-wrap items-center gap-2 lg:gap-4">
                    {/* Previous Month Button */}
                    <button
                      onClick={() => navigateMonth(-1)}
                      className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                      title="Previous month"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>

                    {/* Month and Year Selectors */}
                    <div className="flex items-center space-x-1 lg:space-x-3">
                      <div className="relative flex-shrink-0">
                        <select
                          value={currentMonth}
                          onChange={handleMonthChange}
                          className="appearance-none px-2 sm:px-3 lg:px-4 py-2 pr-8 sm:pr-10 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary font-medium min-w-[85px] sm:min-w-[105px] lg:min-w-[135px] text-xs sm:text-sm lg:text-base [&::-webkit-calendar-picker-indicator]:hidden [&::-ms-expand]:hidden"
                          style={{ backgroundImage: 'none' }}
                        >
                          {monthNames?.map((month, index) => (
                            <option key={index} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-1 sm:pr-2 pointer-events-none">
                          <Icon
                            name="ChevronDown"
                            size={14}
                            className="sm:w-4 sm:h-4"
                            color="var(--color-text-secondary)"
                          />
                        </div>
                      </div>

                      <div className="relative flex-shrink-0">
                        <select
                          value={currentYear}
                          onChange={handleYearChange}
                          className="appearance-none px-2 sm:px-3 lg:px-4 py-2 pr-8 sm:pr-10 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary font-medium min-w-[65px] sm:min-w-[85px] lg:min-w-[105px] text-xs sm:text-sm lg:text-base [&::-webkit-calendar-picker-indicator]:hidden [&::-ms-expand]:hidden"
                          style={{ backgroundImage: 'none' }}
                        >
                          {yearOptions?.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-1 sm:pr-2 pointer-events-none">
                          <Icon
                            name="ChevronDown"
                            size={14}
                            className="sm:w-4 sm:h-4"
                            color="var(--color-text-secondary)"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Next Month Button */}
                    <button
                      onClick={() => navigateMonth(1)}
                      className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                      title="Next month"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </div>

                  {/* Right side - Today and Add Leave Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={goToToday}
                      className="px-2 sm:px-3 lg:px-4 py-2 text-primary bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200 text-xs sm:text-sm lg:text-base flex-shrink-0"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setIsAddLeaveModalOpen(true)}
                      className="px-2 sm:px-3 lg:px-4 py-2 text-white bg-primary hover:bg-primary-600 rounded-lg transition-colors duration-200 text-xs sm:text-sm lg:text-base flex-shrink-0 flex items-center gap-1"
                    >
                      <Icon name="Plus" size={14} />
                      Add Leave
                    </button>
                  </div>
                </div>

                {/* Horizontal Divider */}
                <div className="border-b border-border"></div>

                {/* Calendar Filters */}
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 lg:gap-4 overflow-x-auto">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="eventCelebration"
                      checked={calendarFilters?.eventCelebration}
                      onChange={(e) => handleFilterChange('eventCelebration', e?.target?.checked)}
                      className="w-4 h-4 text-blue-600 bg-background border-border rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label
                      htmlFor="eventCelebration"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Event Celebration
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="fullHoliday"
                      checked={calendarFilters?.fullHoliday}
                      onChange={(e) => handleFilterChange('fullHoliday', e?.target?.checked)}
                      className="w-4 h-4 text-red-600 bg-background border-border rounded focus:ring-red-500 focus:ring-2"
                    />
                    <label
                      htmlFor="fullHoliday"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Full Holiday
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="halfHoliday"
                      checked={calendarFilters?.halfHoliday}
                      onChange={(e) => handleFilterChange('halfHoliday', e?.target?.checked)}
                      className="w-4 h-4 text-orange-600 bg-background border-border rounded focus:ring-orange-500 focus:ring-2"
                    />
                    <label
                      htmlFor="halfHoliday"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Half Holiday
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="myLeaves"
                      checked={calendarFilters?.myLeaves}
                      onChange={(e) => handleFilterChange('myLeaves', e?.target?.checked)}
                      className="w-4 h-4 text-purple-600 bg-background border-border rounded focus:ring-purple-500 focus:ring-2"
                    />
                    <label
                      htmlFor="myLeaves"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      My Leaves
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="saturdayOff"
                      checked={calendarFilters?.saturdayOff}
                      onChange={(e) => handleFilterChange('saturdayOff', e?.target?.checked)}
                      className="w-4 h-4 text-green-600 bg-background border-border rounded focus:ring-green-500 focus:ring-2"
                    />
                    <label
                      htmlFor="saturdayOff"
                      className="text-sm text-text-secondary cursor-pointer"
                    >
                      Saturday Off
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Calendar Grid */}
            <motion.div variants={itemVariants}>
              <SkeletonWrapper
                width="w-full"
                height="h-[600px]"
                isLoading={holidaysLoading || employeeLeavesLoading}
              >
                <CalendarGrid
                  currentDate={currentDate}
                  holidays={holidaysData?.holidays}
                  leaves={employeeLeaves}
                  onDateClick={handleDateClick}
                  calendarFilters={calendarFilters}
                />
              </SkeletonWrapper>
            </motion.div>

            {/* Instructions */}
            <motion.div
              className="mt-4 lg:mt-6 bg-secondary-50 border border-secondary-200 rounded-lg p-3 lg:p-4"
              variants={itemVariants}
            >
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} color="var(--color-text-secondary)" />
                <div className="text-xs lg:text-sm text-text-secondary">
                  <p className="font-medium mb-1">Information:</p>
                  <ul className="space-y-1">
                    <li>• Click on dates with events to view event details</li>
                    <li>• Click on empty dates to apply for leave</li>
                    <li>• Purple markers show your accepted leaves</li>
                    <li>• Use filters to control what appears on calendar</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Panel Toggle Button */}
          <button
            onClick={() => setIsRightPanelOpen(!isRightPanelOpen)}
            className={`absolute top-20 z-40 bg-primary text-white p-3 shadow-lg hover:bg-primary-600 transition-all duration-300 ${
              isRightPanelOpen ? 'right-4 lg:right-80 rounded-l-lg' : 'right-4 rounded-l-lg'
            }`}
            title={isRightPanelOpen ? 'Hide Leave Management Panel' : 'Show Leave Management Panel'}
          >
            <Icon name={isRightPanelOpen ? 'ChevronRight' : 'ChevronLeft'} size={20} />
          </button>

          {/* Mobile Overlay */}
          {isRightPanelOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
              onClick={() => setIsRightPanelOpen(false)}
            />
          )}

          {/* Right Panel - Leave Summary */}
          <div
            className={`transition-all duration-300 ease-in-out ${
              isRightPanelOpen
                ? 'w-80 opacity-100 translate-x-0'
                : 'w-0 opacity-0 translate-x-full overflow-hidden'
            } ${
              // Desktop: flex-shrink-0, Mobile: fixed drawer
              'hidden lg:block lg:flex-shrink-0'
            }`}
          >
            <div
              className={`transition-all duration-300 ease-in-out h-full ${
                isRightPanelOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
              }`}
            >
              <div className="h-full overflow-y-auto">
                <LeaveSummaryPanel
                  leaves={leaves}
                  employeeLeavesStats={employeeLeavesStats}
                  selectedFilter={selectedLeaveFilter}
                  onFilterChange={setSelectedLeaveFilter}
                  onCancelLeave={isAdmin ? null : setRejectLeave}
                  onEditLeave={isAdmin ? null : handleEditLeave}
                  onApproveLeave={isAdmin ? setApproveLeave : null}
                  onRejectLeave={isAdmin ? setRejectLeaveAdmin : null}
                  currentYear={currentYear}
                  isAdmin={isAdmin}
                  isLoading={isCancelingLeave || isApprovingLeave || isRejectingLeave}
                  processingId={processingId}
                />
              </div>
            </div>
          </div>

          {/* Mobile Drawer */}
          {isRightPanelOpen && (
            <div className="lg:hidden fixed right-0 top-16 bottom-0 w-80 bg-surface border-l border-border z-40 transform transition-transform duration-300 ease-in-out overflow-y-auto">
              <LeaveSummaryPanel
                leaves={leaves}
                selectedFilter={selectedLeaveFilter}
                onFilterChange={setSelectedLeaveFilter}
                onCancelLeave={isAdmin ? null : setRejectLeave}
                onEditLeave={isAdmin ? null : handleEditLeave}
                onApproveLeave={isAdmin ? setApproveLeave : null}
                onRejectLeave={isAdmin ? setRejectLeaveAdmin : null}
                currentYear={currentYear}
                isAdmin={isAdmin}
                isLoading={isCancelingLeave || isApprovingLeave || isRejectingLeave}
                processingId={processingId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Event List Modal */}
      <EventListModal
        isOpen={isListModalOpen}
        onClose={() => setIsListModalOpen(false)}
        selectedDate={selectedDate}
        events={selectedEvents}
        leaves={selectedLeaves}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onAdd={handleAddFromList}
        isAdmin={isAdmin}
      />

      <RejectLeaveModal
        isOpen={!!rejectLeave}
        onClose={() => {
          setRejectLeave(null);
        }}
        onConfirm={handleCancelLeave}
        leaveDetails={rejectLeave}
        type="cancel"
      />

      <RejectLeaveModal
        isOpen={!!rejectLeaveAdmin}
        onClose={() => {
          setRejectLeaveAdmin(null);
        }}
        onConfirm={handleRejectLeaveAdmin}
        leaveDetails={rejectLeaveAdmin}
        type="reject"
      />

      <ConfirmationModal
        isOpen={!!approveLeave}
        onClose={() => setApproveLeave(null)}
        onConfirm={handleApproveLeave}
        title="Approve Leave Request"
        message={`Are you sure you want to approve the leave request for ${approveLeave?.user?.first_name} ${approveLeave?.user?.last_name}?`}
        confirmLabel="Approve"
        cancelLabel="Cancel"
        type="success"
      />

      {/* Add Leave Modal */}
      <AddLeaveModal
        isOpen={isAddLeaveModalOpen}
        onClose={() => {
          setIsAddLeaveModalOpen(false);
          setLeaveToEdit(null);
        }}
        isEditMode={!!leaveToEdit}
        leaveToEdit={leaveToEdit}
        selectedDate={selectedDate}
        onAddLeave={handleAddLeave}
        onUpdateLeave={handleUpdateLeave}
        isSubmitting={isAddingLeave || isUpdatingLeave}
      />
    </div>
  );
};

export default HolidayManagementEmployeeView;
